import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../app/hooks';
import ProjectSecretaryBrand from '../../components/ProjectSecretaryBrand/ProjectSecretaryBrand';
import StepLogin from '../../components/steps/StepLogin/StepLogin';
import { trackAnalyticsEvent } from '../../helpers/analytics';
import { selectLoginCredential } from '../../reducers/globalSlice';
import styles from './SignIn.module.scss';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const loginCredential = useAppSelector(selectLoginCredential);
  const businessId = useAppSelector(state => state.business.business?.businessId ?? 0);
  const pageRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (loginCredential && businessId > 0) {
      navigate('/portal', { replace: true });
    }
  }, [businessId, loginCredential, navigate]);

  useEffect(() => {
    document.title = 'Sign in | Project Secretary';
  }, []);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const timeoutIds: number[] = [];

    const updateMobileViewportSizing = () => {
      window.requestAnimationFrame(() => {
        const page = pageRef.current;
        if (!page) {
          return;
        }

        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const headerHeight = headerRef.current?.getBoundingClientRect().height ?? 0;

        if (viewportHeight > 0) {
          page.style.setProperty('--sign-in-app-height', `${Math.round(viewportHeight)}px`);
        }

        if (headerHeight > 0) {
          page.style.setProperty('--sign-in-fixed-offset', `${Math.ceil(headerHeight)}px`);
        }
      });
    };

    updateMobileViewportSizing();
    timeoutIds.push(window.setTimeout(updateMobileViewportSizing, 120));
    timeoutIds.push(window.setTimeout(updateMobileViewportSizing, 420));

    window.addEventListener('resize', updateMobileViewportSizing);
    window.visualViewport?.addEventListener('resize', updateMobileViewportSizing);
    window.visualViewport?.addEventListener('scroll', updateMobileViewportSizing);

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      window.removeEventListener('resize', updateMobileViewportSizing);
      window.visualViewport?.removeEventListener('resize', updateMobileViewportSizing);
      window.visualViewport?.removeEventListener('scroll', updateMobileViewportSizing);
    };
  }, []);

  return (
    <main ref={pageRef} className={styles.page}>
      <div className={styles.background} aria-hidden="true" />
      <header ref={headerRef} className={styles.header}>
        <ProjectSecretaryBrand className={styles.brandLink} />
        <Link
          to="/quick-setup"
          className={styles.setupLink}
          aria-label="Create account"
          onClick={() => trackAnalyticsEvent('registration_started', {
            source: 'sign_in_header',
          })}
        >
          <i className={`fas fa-user-plus ${styles.setupLinkIcon}`} aria-hidden="true" />
          <span className={styles.setupLinkText}>Create account</span>
        </Link>
      </header>

      <section className={styles.shell}>
        <StepLogin
          className={styles.signInFrame}
          authPanelClassName={styles.signInAuthPanel}
          modeScope="loginOnly"
          frameVariant="bare"
          showIntro={false}
          stepLabel="Sign in"
          title="Sign in to your portal"
          description="Use your email and password or Google account to open your Secretary workspace."
          inlineNote="New to Project Secretary? Use the quick setup page to create an account and generate your starter Secretary."
        />
      </section>
    </main>
  );
};

export default SignIn;
